import {
	Button,
	Menu,
	MenuGroup,
	MenuGroupHeader,
	MenuItemCheckbox,
	MenuList,
	MenuPopover,
	MenuTrigger,
	PositioningImperativeRef,
	SortDirection,
	TableColumnDefinition,
	TableColumnId,
	TableHeaderCell,
	Tooltip,
} from "@fluentui/react-components";
import * as React from "react";
import {
	Filter16Regular as FilterIcon,
	ChevronDown16Regular as ChevronUpIcon,
	ArrowSortDown16Regular as ArrowSortDownIcon,
	ArrowSortUp16Regular as ArrowSortUpIcon,
} from "@fluentui/react-icons";
import { ColumnSizingTableCellProps, FilterValue, IColumnDefinition } from "./PCSTableTypes";
import styles from "./PCSTable.module.scss";
export interface IPCSTableHeaderCellProps<T extends object> {
	columnDefinition: IColumnDefinition<T>;
	tabeleColumnDefinition: TableColumnDefinition<T>;
	getSortDirection: (columnId: TableColumnId) => SortDirection | undefined;
	toggleColumnSort: (event: React.SyntheticEvent<Element, Event>, columnId: TableColumnId) => void;
	setColumnSort: (
		event: React.SyntheticEvent<Element, Event>,
		columnId: TableColumnId,
		sortDirection: SortDirection
	) => void;
	toggleFilter: (filter: FilterValue, columnId: IColumnDefinition<T>) => void;
	headerSizing: ColumnSizingTableCellProps;
	filters: FilterValue[];
	selectedFilters: FilterValue[];
	setColumnFilters: (filters: FilterValue[], columnId: IColumnDefinition<T>) => void;
}

export function PCSTableHeaderCell<T extends object>({
	columnDefinition,
	tabeleColumnDefinition: col,
	getSortDirection,
	toggleColumnSort,
	setColumnSort,
	toggleFilter,
	headerSizing,
	filters,
	selectedFilters,
	setColumnFilters,
}: IPCSTableHeaderCellProps<T>) {
	const headerSortProps = (columnId: TableColumnId) => ({
		onClick: (e: React.MouseEvent) => {
			toggleColumnSort(e, columnId);
		},
		sortDirection: getSortDirection(columnId),
	});
	const [showMenu, setShowMenu] = React.useState(false);
	const sortProps = headerSortProps(columnDefinition.columnId);
	const hasContextMenu = columnDefinition.filterable;
	const cellDiv = React.useRef<HTMLDivElement>(null);
	const [showTooltip, setShowTooltip] = React.useState(false);
	const hasOverflow = (cellDiv.current?.offsetWidth ?? 0) < (cellDiv.current?.scrollWidth ?? 0);
	const headerCellRef = React.useRef<HTMLTableCellElement>(null);
	const positioningRef = React.useRef<PositioningImperativeRef>(null);
	React.useEffect(() => {
		if (headerCellRef.current) {
			positioningRef.current?.setTarget(headerCellRef.current);
		}
	}, [headerCellRef, positioningRef]);

	return (
		<>
			<TableHeaderCell ref={headerCellRef} key={columnDefinition.columnId} {...headerSizing}>
				<div
					onClick={(ev) => {
						if (columnDefinition.sortable) {
							toggleColumnSort(ev, columnDefinition.columnId);
						}
					}}
					className={styles.headerCell}>
					<Tooltip
						relationship="label"
						positioning={"below"}
						visible={showTooltip}
						onVisibleChange={(ev, context) => {
							if (hasOverflow) {
								setShowTooltip(context.visible);
							} else {
								setShowTooltip(false);
							}
						}}
						content={<div>{columnDefinition.headerText}</div>}>
						<div ref={cellDiv} className={styles.headerText}>
							{col.renderHeaderCell()}
						</div>
					</Tooltip>
					{!!columnDefinition.sortable && !!sortProps.sortDirection && (
						<div className={styles.icon}>
							{sortProps.sortDirection === "ascending" ? (
								<ArrowSortUpIcon />
							) : (
								<ArrowSortDownIcon />
							)}
						</div>
					)}
					{columnDefinition.filterable && selectedFilters.length > 0 && (
						<Tooltip relationship="label" content={"Clear Filters"}>
							<div
								className={styles.icon}
								onClick={(ev) => {
									ev.stopPropagation();
									setColumnFilters([], columnDefinition);
								}}>
								<FilterIcon />
							</div>
						</Tooltip>
					)}
					{hasContextMenu && (
						<div
							onClick={(ev) => {
								setShowMenu(!showMenu);
								ev.stopPropagation();
							}}
							className={styles.icon}>
							<ChevronUpIcon />
						</div>
					)}
				</div>
				<Menu
					positioning={{ positioningRef }}
					open={showMenu && hasContextMenu}
					onOpenChange={(ev, data) => {
						setShowMenu(data.open);
					}}
					checkedValues={{
						sort: sortProps.sortDirection ? [sortProps.sortDirection] : [],
						filter: selectedFilters.map((i) => i.key),
					}}>
					<MenuPopover>
						<MenuList>
							{columnDefinition.sortable && (
								<MenuGroup>
									<MenuGroupHeader>Sort</MenuGroupHeader>
									<MenuItemCheckbox
										onClick={(ev) => {
											setColumnSort(ev, columnDefinition.columnId, "ascending");
										}}
										name="sort"
										value="ascending">
										Ascending
									</MenuItemCheckbox>
									<MenuItemCheckbox
										name="sort"
										value="descending"
										onClick={(ev) => {
											setColumnSort(ev, columnDefinition.columnId, "descending");
										}}>
										Descending
									</MenuItemCheckbox>
								</MenuGroup>
							)}
							{columnDefinition.filterable && filters.length > 0 && (
								<MenuGroup>
									<MenuGroupHeader>Filter</MenuGroupHeader>
									{filters.map((i) => (
										<MenuItemCheckbox
											onClick={() => {
												toggleFilter(i, columnDefinition);
											}}
											name="filter"
											key={i.key}
											value={i.key}>
											<span className={styles.filterLabel} title={i.label}>
												{i.label}
											</span>
										</MenuItemCheckbox>
									))}
								</MenuGroup>
							)}
						</MenuList>
					</MenuPopover>
				</Menu>
			</TableHeaderCell>
		</>
	);
}
